/* eslint-disable react-hooks/exhaustive-deps */

import { PageChangeDetails } from 'node_modules/@chakra-ui/react/dist/types/components/pagination/namespace';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { debounce } from 'lodash';

import {
  Box,
  Button,
  Flex,
  Image,
  Menu,
  Portal,
  Skeleton,
  Spinner,
  Stack,
  Table,
  Text
} from '@chakra-ui/react';

import { CaretLeft, CaretRight, Copy, DotsThree, Plus } from '@phosphor-icons/react';

import { toaster } from '@/components/ui/toaster';
import { fetchAPI } from '@/api/fetch';

import {
  apiArchiveClient,
  apiGetClient,
  apiGetClients,
  apiGetJobs,
  apiGetJobSummary,
  apiGetPublishedJobs,
  apiUpdateJobPublishJobBoard,
  apiUpdateJobStatus,
  GetClientsResponse,
  GetJobsResponse,
  GetJobSummaryResponse
} from '@/api/services';

import { JobBasicInfo } from '@/types/Job';
import { Client } from '@/types/Client';
import { getJobStatusColor, getJobStatusLabel, JobStatus } from '@/types/enums';

import {
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot
} from '@/components/ui/pagination';

import { SearchBox } from '@/components/ui/searchbox';

import emptyJobImage from '@/assets/image/empty-job-cover.svg';

import ArchiveDialog, { ArchiveType } from './ArchiveDialog';
import EditClientProfile from './EditClientProfile';
import PublishJobDialog from './Details/PublishJobDialog';

import useAuth from '@/store/auth';
import { getJobBoardUrl, openInNewTab } from '@/utils';

interface SummaryCardProps {
  label: string;
  count: number;
  isLoading: boolean;
}

interface TableHeaderProps {
  headers: string[];
}

interface JobTableRowProps {
  job: JobBasicInfo;
  onArchive?: (job: JobBasicInfo) => void;
  isArchived?: boolean;
  onNavigate: (jobId: string) => void;
  onPublish?: (job: JobBasicInfo, publish: boolean, updateCategory: boolean) => void;
}

interface JobBoardTableRowProps {
  job: JobBasicInfo;
  onNavigate: (jobId: string) => void;
  onPublish: (job: JobBasicInfo, publish: boolean, updateCategory: boolean) => void;
}

interface ClientTableRowProps {
  client: Client;
  onSelect: (client: Client) => void;
  onArchive: (client: Client) => void;
  onEdit: (client: Client) => void;
  isArchived: boolean;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ label, count, isLoading }) => {
  if (isLoading) {
    return <Skeleton height="120px" borderRadius="8px" flex="1" />;
  }

  return (
    <Box
      bg="background.secondary"
      border="1px solid"
      borderColor="neutrals.300"
      borderRadius="8px"
      flex="1"
      h="120px"
      boxShadow="sm"
      _hover={{ boxShadow: 'md' }}
      transition="all 0.2s ease-in-out"
    >
      <Flex flexDir="column" justifyContent="top" alignItems="left" h="100%" p="14px 24px">
        <Text fontSize="18px" fontWeight="regular" mb="8px">
          {label}
        </Text>
        <Text fontSize="40px" fontWeight="bold" color="neutrals.700">
          {count}
        </Text>
      </Flex>
    </Box>
  );
};

const TableHeader: React.FC<TableHeaderProps> = ({ headers }) => (
  <Table.Row bg="neutrals.50" h="48px">
    {headers.map((header, index) => (
      <Table.ColumnHeader
        key={header}
        fontSize="14px"
        fontWeight="bold"
        borderColor="neutrals.300"
        pl={index === 0 ? '32px' : 0}
        w="auto"
      >
        <Box>{header}</Box>
      </Table.ColumnHeader>
    ))}
    <Table.ColumnHeader
      fontSize="14px"
      fontWeight="bold"
      borderColor="neutrals.300"
      textAlign="end"
      w="50px"
    />
  </Table.Row>
);

const JobTableRow: React.FC<JobTableRowProps> = ({
  job,
  onArchive,
  onNavigate,
  onPublish,
  isArchived = false
}) => (
  <Table.Row key={job.job_id} _hover={{ cursor: 'pointer', bg: 'neutrals.50' }}>
    {['unique_id', 'job_title', 'created_at', 'status'].map((field, index) => (
      <Table.Cell key={field} fontSize="14px" p="0" onClick={() => onNavigate(job.job_id)}>
        {field === 'created_at' ? (
          <Box>{new Date(job.created_at * 1000).toLocaleDateString()}</Box>
        ) : field === 'status' ? (
          <Box
            color={getJobStatusColor(job.status)}
            border="1px solid"
            borderColor={getJobStatusColor(job.status)}
            borderRadius="8px"
            p="4px 8px"
            w="fit-content"
            textAlign="center"
            fontSize="12px"
            textTransform="uppercase"
          >
            {getJobStatusLabel(job.status)}
          </Box>
        ) : (
          <Box pl={index === 0 ? '32px' : 0}>{job[field as 'unique_id' | 'job_title']}</Box>
        )}
      </Table.Cell>
    ))}
    <Table.Cell fontSize="16px" textAlign="end" py="4px">
      <Box display="flex" justifyContent="flex-end" alignItems="center">
        <Menu.Root>
          <Menu.Trigger asChild>
            <Button variant="ghost" borderRadius="full">
              <DotsThree size="24px" />
            </Button>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content>
                {!job.publish_job_board && !isArchived && (
                  <Menu.Item
                    value="publish"
                    onClick={() => onPublish && onPublish(job, true, false)}
                    _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                    fontSize="14px"
                    p="8px 12px"
                    textAlign="left"
                  >
                    Publish to Job Board
                  </Menu.Item>
                )}
                <Menu.Item
                  value="archive"
                  onClick={() => onArchive && onArchive(job)}
                  _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                  fontSize="14px"
                  p="8px 12px"
                  textAlign="left"
                >
                  {isArchived ? 'Restore' : 'Archive'}
                </Menu.Item>
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      </Box>
    </Table.Cell>
  </Table.Row>
);

const JobBoardTableRow: React.FC<JobBoardTableRowProps> = ({ job, onNavigate, onPublish }) => (
  <Table.Row key={job.job_id} _hover={{ cursor: 'pointer', bg: 'neutrals.50' }}>
    {['unique_id', 'category', 'client', 'job_title'].map((field, index) => (
      <Table.Cell key={field} fontSize="14px" p="0" onClick={() => onNavigate(job.job_id)}>
        {field === 'client' ? (
          <Box>{job.client.name}</Box>
        ) : (
          <Box pl={index === 0 ? '32px' : 0}>{job[field as 'unique_id' | 'job_title']}</Box>
        )}
      </Table.Cell>
    ))}
    <Table.Cell fontSize="16px" textAlign="end" py="4px">
      <Box display="flex" justifyContent="flex-end" alignItems="center">
        <Menu.Root>
          <Menu.Trigger asChild>
            <Button variant="ghost" borderRadius="full">
              <DotsThree size="24px" />
            </Button>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content>
                <Menu.Item
                  value="update-category"
                  onClick={() => onPublish(job, true, true)}
                  _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                  fontSize="14px"
                  p="8px 12px"
                  textAlign="left"
                >
                  Update Category
                </Menu.Item>
                <Menu.Item
                  value="archive"
                  onClick={() => onPublish(job, false, false)}
                  _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                  fontSize="14px"
                  p="8px 12px"
                  textAlign="left"
                >
                  Unpublish
                </Menu.Item>
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      </Box>
    </Table.Cell>
  </Table.Row>
);

const ClientTableRow: React.FC<ClientTableRowProps> = ({
  client,
  onSelect,
  onArchive,
  onEdit,
  isArchived
}) => (
  <Table.Row key={client.client_id} _hover={{ bg: 'neutrals.50' }}>
    {(['unique_id', 'name', 'industry'] as Array<keyof Client>).map((field, index) => (
      <Table.Cell
        key={field}
        fontSize="14px"
        p="0"
        _hover={{ cursor: 'pointer' }}
        onClick={() => onSelect(client)}
      >
        <Box pl={index === 0 ? '32px' : 0}>{client[field]}</Box>
      </Table.Cell>
    ))}
    <Table.Cell fontSize="16px" textAlign="end" py="4px">
      <Box display="flex" justifyContent="flex-end" alignItems="center">
        <Menu.Root composite>
          <Menu.Trigger asChild>
            <Button variant="ghost" borderRadius="full">
              <DotsThree size="24px" />
            </Button>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content>
                <Menu.Item
                  value="archive"
                  onClick={() => onArchive(client)}
                  _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                  fontSize="14px"
                  p="8px 12px"
                  textAlign="left"
                >
                  {isArchived ? 'Restore' : 'Archive'}
                </Menu.Item>
                {!isArchived && (
                  <Menu.Item
                    value="edit"
                    onClick={() => onEdit(client)}
                    _hover={{ cursor: 'pointer', bg: 'neutrals.100' }}
                    fontSize="14px"
                    p="8px 12px"
                    textAlign="left"
                  >
                    Edit
                  </Menu.Item>
                )}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      </Box>
    </Table.Cell>
  </Table.Row>
);

export default function JobIndex() {
  const auth = useAuth();

  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client_id');
  const seeJobBoard = searchParams.get('job_board') === 'true';
  const pageSize = 10;
  const [pageLoading, setPageLoading] = useState(true);
  const [listLoading, setListLoading] = useState(true);
  const [activeJobs, setActiveJobs] = useState(0);
  const [jobsAwaitingReview, setJobsAwaitingReview] = useState(0);
  const [candidatesInPipeline, setCandidatesInPipeline] = useState(0);
  const [clients, setClients] = useState<Client[]>([]);
  const [clientCurrentPage, setClientCurrentPage] = useState(1);
  const [clientTotalPages, setClientTotalPages] = useState(1);
  const [clientKeyword, setClientKeyword] = useState('');
  const [jobKeyword, setJobKeyword] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [jobs, setJobs] = useState<JobBasicInfo[]>([]);
  const [jobCurrentPage, setJobCurrentPage] = useState(1);
  const [jobTotalPages, setJobTotalPages] = useState(1);
  const [seeArchivedClient, setSeeArchivedClient] = useState(false);
  const [seeArchivedJob, setSeeArchivedJob] = useState(false);
  const [fetchDataError, setFetchDataError] = useState(false);
  const [editClientProfileOpen, setEditClientProfileOpen] = useState(false);
  const [editClientProfileClient, setEditClientProfileClient] = useState<Client | null>(null);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveType, setArchiveType] = useState<ArchiveType>(ArchiveType.Job);
  const [archiveItem, setArchiveItem] = useState<Client | JobBasicInfo>();
  const [isArchived, setIsArchived] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishJob, setPublishJob] = useState<JobBasicInfo | null>(null);
  const [isPublishUpdating, setIsPublishUpdating] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [toPublish, setToPublish] = useState(false);
  const [toUpdateCategory, setToUpdateCategory] = useState(false);

  const navigate = useNavigate();

  const handleArchiveDialogOpen = (type: ArchiveType, item: Client | JobBasicInfo) => {
    setArchiveType(type);
    setArchiveItem(item);
    setIsArchived(seeArchivedClient || seeArchivedJob);
    setArchiveDialogOpen(true);
  };

  useEffect(() => {
    const loadJobSummary = async () => {
      setPageLoading(true);
      try {
        const [jobSummaryResponse, clientResponse] = await Promise.all([
          fetchAPI<GetJobSummaryResponse>({ request: apiGetJobSummary }),
          fetchAPI<GetClientsResponse>({
            request: () =>
              apiGetClients({
                page: clientCurrentPage,
                limit: pageSize,
                keyword: clientKeyword
              })
          })
        ]);

        if (jobSummaryResponse.success) {
          const { active_jobs, jobs_awaiting_review, candidates_in_pipeline } =
            jobSummaryResponse.data;
          setActiveJobs(active_jobs);
          setJobsAwaitingReview(jobs_awaiting_review);
          setCandidatesInPipeline(candidates_in_pipeline);
        }

        if (clientResponse.success) {
          const { clients, current_page, total_page } = clientResponse.data;
          setClients(clients);
          setClientCurrentPage(current_page);
          setClientTotalPages(total_page);
        } else {
          console.error('Failed to fetch clients:', clientResponse.message);
          setFetchDataError(true);
        }
      } catch (error) {
        console.error('Failed to fetch job summary or clients:', error);
        setFetchDataError(true);
      } finally {
        setPageLoading(false);
      }
    };

    loadJobSummary();
  }, []);

  useEffect(() => {
    const loadClientJobs = async (client_id: string) => {
      setListLoading(true);
      setSelectedClient(null);

      try {
        const response = await fetchAPI<GetJobsResponse>({
          request: () => apiGetJobs({ params: { client_id, page: 1, limit: pageSize } })
        });

        if (response.success) {
          const { jobs, current_page, total_page } = response.data;
          setJobs(jobs);
          setJobCurrentPage(current_page);
          setJobTotalPages(total_page);
        } else {
          console.error('Failed to fetch jobs:', response.message);
          navigate('/job');
        }

        const clientResponse = await fetchAPI<Client>({
          request: () => apiGetClient(client_id)
        });

        if (clientResponse.success) {
          setSelectedClient(clientResponse.data);
        } else {
          console.error('Failed to fetch client:', clientResponse.message);
          navigate('/job');
        }
      } catch (error) {
        console.error('Failed to fetch jobs:', error);
        navigate('/job');
      } finally {
        setListLoading(false);
      }
    };

    if (clientId) {
      loadClientJobs(clientId);
    }
  }, [clientId]);

  useEffect(() => {
    const loadClient = async () => {
      setListLoading(true);
      setFetchDataError(false);
      setClientKeyword('');
      setClientCurrentPage(1);

      try {
        const response = await fetchAPI<GetClientsResponse>({
          request: () =>
            apiGetClients({
              page: 1,
              limit: pageSize,
              archived: seeArchivedClient
            })
        });

        if (response.success) {
          const { clients, current_page, total_page } = response.data;
          setClients(clients);
          setClientCurrentPage(current_page);
          setClientTotalPages(total_page);
        } else {
          console.error('Failed to fetch clients:', response.message);
          setFetchDataError(true);
        }
      } catch (error) {
        console.error('Failed to fetch clients:', error);
        setFetchDataError(true);
      } finally {
        setListLoading(false);
      }
    };

    loadClient();
  }, [seeArchivedClient]);

  useEffect(() => {
    const loadJob = async () => {
      if (!clientId) return;
      setListLoading(true);
      setFetchDataError(false);
      setJobKeyword('');
      setJobCurrentPage(1);

      try {
        const response = await fetchAPI<GetJobsResponse>({
          request: () =>
            apiGetJobs({
              params: {
                client_id: clientId,
                page: 1,
                limit: pageSize,
                archived: seeArchivedJob
              }
            })
        });

        if (response.success) {
          const { jobs, current_page, total_page } = response.data;
          setJobs(jobs);
          setJobCurrentPage(current_page);
          setJobTotalPages(total_page);
        } else {
          console.error('Failed to fetch jobs:', response.message);
          setFetchDataError(true);
        }
      } catch (error) {
        console.error('Failed to fetch jobs:', error);
        setFetchDataError(true);
      } finally {
        setListLoading(false);
      }
    };

    loadJob();
  }, [seeArchivedJob]);

  useEffect(() => {
    const loadJobBoardJobs = async () => {
      if (!seeJobBoard) return;
      setListLoading(true);
      setFetchDataError(false);

      try {
        const response = await fetchAPI<GetJobsResponse>({
          request: () =>
            apiGetPublishedJobs({
              params: {
                page: jobCurrentPage,
                limit: pageSize,
                keyword: jobKeyword
              }
            })
        });

        if (response.success) {
          const { jobs, current_page, total_page } = response.data;
          setJobs(jobs);
          setJobCurrentPage(current_page);
          setJobTotalPages(total_page);
        } else {
          console.error('Failed to fetch job board jobs:', response.message);
          setFetchDataError(true);
        }
      } catch (error) {
        console.error('Failed to fetch job board jobs:', error);
        setFetchDataError(true);
      } finally {
        setListLoading(false);
      }
    };

    loadJobBoardJobs();
  }, [seeJobBoard, jobCurrentPage, pageSize, jobKeyword]);

  const searchClient = useCallback(
    debounce(async (keyword: string) => {
      setListLoading(true);
      setFetchDataError(false);
      setClientKeyword(keyword);
      setClientCurrentPage(1);

      try {
        const response = await fetchAPI<GetClientsResponse>({
          request: () =>
            apiGetClients({
              page: 1,
              limit: pageSize,
              keyword,
              archived: seeArchivedClient
            })
        });

        if (response.success) {
          const { clients, current_page, total_page } = response.data;
          setClients(clients);
          setClientCurrentPage(current_page);
          setClientTotalPages(total_page);
        } else {
          console.error('Failed to fetch clients:', response.message);
          setFetchDataError(true);
        }
      } catch (error) {
        console.error('Failed to fetch clients:', error);
        setFetchDataError(true);
      } finally {
        setListLoading(false);
      }
    }, 500),
    []
  );

  const searchJob = useCallback(
    debounce(async (keyword: string) => {
      if (!clientId) return;
      setListLoading(true);
      setFetchDataError(false);
      setJobKeyword(keyword);
      setJobCurrentPage(1);

      try {
        const response = await fetchAPI<GetJobsResponse>({
          request: () =>
            apiGetJobs({
              params: {
                client_id: clientId,
                page: 1,
                limit: pageSize,
                keyword,
                archived: seeArchivedJob
              }
            })
        });

        if (response.success) {
          const { jobs, current_page, total_page } = response.data;
          setJobs(jobs);
          setJobCurrentPage(current_page);
          setJobTotalPages(total_page);
        } else {
          console.error('Failed to fetch jobs:', response.message);
          setFetchDataError(true);
        }
      } catch (error) {
        console.error('Failed to fetch jobs:', error);
        setFetchDataError(true);
      } finally {
        setListLoading(false);
      }
    }, 500),
    [clientId, pageSize]
  );

  const searchJobBoard = useCallback(
    debounce(async (keyword: string) => {
      if (!seeJobBoard) return;
      setListLoading(true);
      setFetchDataError(false);
      setJobKeyword(keyword);
      setJobCurrentPage(1);

      try {
        const response = await fetchAPI<GetJobsResponse>({
          request: () =>
            apiGetPublishedJobs({
              params: {
                page: 1,
                limit: pageSize,
                keyword
              }
            })
        });

        if (response.success) {
          const { jobs, current_page, total_page } = response.data;
          setJobs(jobs);
          setJobCurrentPage(current_page);
          setJobTotalPages(total_page);
        } else {
          console.error('Failed to search job board jobs:', response.message);
          setFetchDataError(true);
        }
      } catch (error) {
        console.error('Failed to search job board jobs:', error);
        setFetchDataError(true);
      } finally {
        setListLoading(false);
      }
    }, 500),
    [seeJobBoard, pageSize]
  );

  const selectClient = (client: Client) => {
    navigate(`/job?client_id=${client.client_id}`);
  };

  const archiveClient = async (client: Client, archived: boolean): Promise<number> => {
    try {
      const response = await fetchAPI<void>({
        request: () =>
          apiArchiveClient(client.client_id, {
            archived: archived
          })
      });

      if (response.success) {
        const newClients = clients.filter((c) => c.client_id !== client.client_id);
        setClients(newClients);
      }

      return response.code;
    } catch (error) {
      console.error('Failed to archive client:', error);
      throw error;
    }
  };

  const archiveJob = async (job: JobBasicInfo, archived: boolean): Promise<number> => {
    try {
      const response = await fetchAPI<void>({
        request: () =>
          apiUpdateJobStatus({
            jobId: job.job_id,
            payload: {
              status: archived ? JobStatus.Archived : JobStatus.InProgress
            }
          })
      });

      if (response.success) {
        const newJobs = jobs.filter((j) => j.job_id !== job.job_id);
        setJobs(newJobs);
      }

      return response.code;
    } catch (error) {
      console.error('Failed to archive job:', error);
      throw error;
    }
  };

  const changeClientsPage = async (details: PageChangeDetails) => {
    setClientCurrentPage(details.page);
    setFetchDataError(false);

    try {
      const response = await fetchAPI<GetClientsResponse>({
        request: () =>
          apiGetClients({
            page: details.page,
            limit: details.pageSize,
            keyword: clientKeyword
          })
      });

      if (response.success) {
        const { clients, current_page, total_page } = response.data;
        setClients(clients);
        setClientCurrentPage(current_page);
        setClientTotalPages(total_page);
      } else {
        console.error('Failed to change client page:', response.message);
        setFetchDataError(true);
      }
    } catch (error) {
      console.error('Failed to change client page:', error);
      setFetchDataError(true);
    }
  };

  const changeJobsPage = async (details: PageChangeDetails) => {
    if (!clientId) return;
    setJobCurrentPage(details.page);
    setFetchDataError(false);

    try {
      const response = await fetchAPI<GetJobsResponse>({
        request: () =>
          apiGetJobs({
            params: {
              page: details.page,
              limit: details.pageSize,
              client_id: clientId,
              keyword: jobKeyword
            }
          })
      });

      if (response.success) {
        const { jobs, current_page, total_page } = response.data;
        setJobs(jobs);
        setJobCurrentPage(current_page);
        setJobTotalPages(total_page);
      } else {
        console.error('Failed to change job page:', response.message);
        setFetchDataError(true);
      }
    } catch (error) {
      console.error('Failed to change job page:', error);
      setFetchDataError(true);
    }
  };

  const changeJobBoardPage = async (details: PageChangeDetails) => {
    if (!seeJobBoard) return;
    setJobCurrentPage(details.page);
    setFetchDataError(false);

    try {
      const response = await fetchAPI<GetJobsResponse>({
        request: () =>
          apiGetPublishedJobs({
            params: {
              page: details.page,
              limit: details.pageSize,
              keyword: jobKeyword
            }
          })
      });

      if (response.success) {
        const { jobs, current_page, total_page } = response.data;
        setJobs(jobs);
        setJobCurrentPage(current_page);
        setJobTotalPages(total_page);
      } else {
        console.error('Failed to change job board page:', response.message);
        setFetchDataError(true);
      }
    } catch (error) {
      console.error('Failed to change job board page:', error);
      setFetchDataError(true);
    }
  };

  const updateClient = (client: Client) => {
    const newClients = clients.map((c) => (c.client_id === client.client_id ? client : c));
    setClients(newClients);
  };

  const handlePublishJob = (job: JobBasicInfo, publish: boolean, updateCategory: boolean) => {
    setPublishJob(job);
    setPublishError(null);
    setPublishDialogOpen(true);
    setToPublish(publish);
    setToUpdateCategory(updateCategory);
  };

  const handlePublishConfirm = async (category?: string) => {
    if (!publishJob) return;

    setIsPublishUpdating(true);
    setPublishError(null);

    try {
      const response = await fetchAPI({
        request: () =>
          apiUpdateJobPublishJobBoard({
            jobId: publishJob.job_id,
            payload: { published: toPublish, category: category }
          })
      });

      if (response.success) {
        // Remove the unpublished job from the job board list
        if (!toPublish) {
          setJobs((prevJobs) => prevJobs.filter((job) => job.job_id !== publishJob.job_id));
        }

        // update category
        if (category) {
          setJobs((prevJobs) =>
            prevJobs.map((job) =>
              job.job_id === publishJob.job_id ? { ...job, category: category } : job
            )
          );
        }

        setPublishDialogOpen(false);
        setPublishJob(null);
        setToPublish(false);
      } else {
        setPublishError(response.message || 'Failed to update job publish status');
      }
    } catch (error) {
      console.error('Error update job publish status:', error);
      setPublishError('An error occurred while updating job publish status');
    } finally {
      setIsPublishUpdating(false);
    }
  };

  const handlePublishCancel = () => {
    setPublishDialogOpen(false);
    setPublishError(null);
    setPublishJob(null);
  };

  const summaryCards = useMemo(
    () => [
      { label: 'Active Jobs', count: activeJobs },
      { label: 'Jobs Awaiting Review', count: jobsAwaitingReview },
      { label: 'Candidates in Pipeline', count: candidatesInPipeline }
    ],
    [activeJobs, jobsAwaitingReview, candidatesInPipeline]
  );

  const handleArchiveJob = useCallback(
    (job: JobBasicInfo) => {
      handleArchiveDialogOpen(ArchiveType.Job, job);
    },
    [handleArchiveDialogOpen]
  );

  const handleArchiveClient = useCallback(
    (client: Client) => {
      handleArchiveDialogOpen(ArchiveType.Client, client);
    },
    [handleArchiveDialogOpen]
  );

  const handleEditClient = useCallback((client: Client) => {
    setEditClientProfileClient(client);
    setEditClientProfileOpen(true);
  }, []);

  const handleNavigateToJob = useCallback(
    (jobId: string) => {
      navigate(`/job/details/${jobId}`);
    },
    [navigate]
  );

  const handleShareJobBoard = useCallback(async () => {
    if (!auth.account?.agency.agency_id) return;

    const jobBoardUrl = `${window.location.origin}${getJobBoardUrl(auth.account.agency.agency_id)}`;

    try {
      await navigator.clipboard.writeText(jobBoardUrl);
      toaster.create({
        description: 'Job board URL copied to clipboard',
        type: 'success',
        duration: 3000
      });
    } catch (error) {
      console.error('Failed to copy job board URL:', error);
      toaster.create({
        description: 'Failed to copy URL to clipboard',
        type: 'error',
        duration: 3000
      });
    }
  }, [auth.account?.agency.agency_id]);

  return (
    <Flex w="100%" flexDir="column" p="40px 48px">
      <Flex justifyContent="space-between" gap="16px">
        {summaryCards.map((item) => (
          <SummaryCard
            key={item.label}
            label={item.label}
            count={item.count}
            isLoading={pageLoading}
          />
        ))}
      </Flex>
      <Box h="20px" />
      {pageLoading ? (
        <Skeleton borderRadius="8px" flex="1" h="120px" />
      ) : (
        <Box
          bg="background.secondary"
          border="1px solid"
          borderColor="neutrals.300"
          borderRadius="8px"
          flex="1"
          h="120px"
          boxShadow="sm"
          _hover={{ boxShadow: 'md' }}
          transition="all 0.2s ease-in-out"
        >
          <Flex flexDir="column" justifyContent="top" alignItems="left" h="100%" p="24px 32px">
            <Flex justify="space-between" mb="20px" alignItems="center" h="36px">
              {seeJobBoard ? (
                <Flex alignItems="center" gap="8px">
                  <Box as="button" onClick={() => navigate('/job')} cursor="pointer">
                    <CaretLeft size={24} weight="bold" color="#545454" />
                  </Box>
                  <Text fontSize="24px" fontWeight="bold" color="neutrals.900">
                    Jobs on Job Board
                  </Text>
                </Flex>
              ) : clientId && selectedClient ? (
                <Stack direction="row" alignItems="center">
                  <Text
                    fontSize="24px"
                    fontWeight="bold"
                    color="neutrals.700"
                    _hover={{ cursor: 'pointer' }}
                    onClick={() => {
                      setSeeArchivedJob(false);
                      setSelectedClient(null);
                      navigate('/job');
                    }}
                  >
                    Client List
                  </Text>
                  <CaretRight size={20} weight="bold" color="#545454" />
                  <Text
                    fontSize="24px"
                    fontWeight="bold"
                    color={seeArchivedJob ? 'neutrals.700' : 'neutrals.900'}
                    onClick={() => setSeeArchivedJob(false)}
                    cursor="pointer"
                  >
                    {selectedClient.name}
                  </Text>
                  {seeArchivedJob && (
                    <Flex alignItems="center" gap="8px">
                      <CaretRight size={20} weight="bold" color="#545454" />
                      <Text fontSize="24px" fontWeight="bold" color="neutrals.900">
                        Archived Jobs
                      </Text>
                    </Flex>
                  )}
                </Stack>
              ) : seeArchivedClient ? (
                <Stack direction="row" alignItems="center">
                  <Text
                    fontSize="24px"
                    fontWeight="bold"
                    color="neutrals.700"
                    _hover={{ cursor: 'pointer' }}
                    onClick={() => setSeeArchivedClient(false)}
                  >
                    All
                  </Text>
                  <CaretRight size={20} weight="bold" color="#545454" />
                  <Text fontSize="24px" fontWeight="bold" color="neutrals.900">
                    Archived Clients
                  </Text>
                </Stack>
              ) : (
                <Text
                  fontSize="24px"
                  fontWeight="bold"
                  color="neutrals.700"
                  _hover={{ cursor: 'pointer' }}
                  onClick={() => {
                    setSeeArchivedJob(false);
                    setSelectedClient(null);
                    navigate('/job');
                  }}
                >
                  Client List
                </Text>
              )}
              {seeJobBoard ? (
                <Flex gap="8px" ml="auto" alignItems="center">
                  <Box
                    as="button"
                    textDecoration="underline"
                    color="neutrals.700"
                    fontSize="18px"
                    h="fit-content"
                    cursor="pointer"
                    fontWeight="bold"
                    _hover={{ color: 'primary.700' }}
                    onClick={() => {
                      setSeeArchivedClient(false);
                      setSeeArchivedJob(false);
                      setSelectedClient(null);
                      if (auth.account?.agency.agency_id) {
                        openInNewTab(getJobBoardUrl(auth.account.agency.agency_id));
                      }
                    }}
                  >
                    View Job Board
                  </Box>
                  <Button
                    variant="ghost"
                    color="neutrals.700"
                    fontSize="16px"
                    h="32px"
                    w="32px"
                    cursor="pointer"
                    fontWeight="bold"
                    _hover={{ color: 'primary.700', bg: 'neutrals.100' }}
                    onClick={handleShareJobBoard}
                  >
                    <Copy size={20} />
                  </Button>
                </Flex>
              ) : (
                <Box
                  as="button"
                  textDecoration="underline"
                  color="neutrals.700"
                  fontSize="18px"
                  ml="auto"
                  h="fit-content"
                  cursor="pointer"
                  fontWeight="bold"
                  _hover={{ color: 'primary.700' }}
                  onClick={() => {
                    setSeeArchivedClient(false);
                    setSeeArchivedJob(false);
                    setSelectedClient(null);
                    navigate(`/job?job_board=true`);
                  }}
                >
                  Job Board
                </Box>
              )}
            </Flex>

            <Flex justifyContent="space-between" alignItems="center">
              <SearchBox
                placeholder={
                  seeJobBoard
                    ? 'Search job title'
                    : clientId
                    ? 'Search job title'
                    : 'Search company name'
                }
                search={seeJobBoard ? searchJobBoard : clientId ? searchJob : searchClient}
              />
              {!seeJobBoard && !(clientId ? seeArchivedJob : seeArchivedClient) && (
                <Flex>
                  <Button
                    borderColor="primary.900"
                    background={
                      clientId
                        ? seeArchivedJob
                          ? 'primary.900'
                          : 'background.secondary'
                        : seeArchivedClient
                        ? 'primary.900'
                        : 'background.secondary'
                    }
                    color={
                      clientId
                        ? seeArchivedJob
                          ? 'neutrals.0'
                          : 'primary.900'
                        : seeArchivedClient
                        ? 'neutrals.0'
                        : 'primary.900'
                    }
                    borderRadius="8px"
                    ml="16px"
                    fontSize="16px"
                    onClick={() =>
                      clientId
                        ? setSeeArchivedJob(!seeArchivedJob)
                        : setSeeArchivedClient(!seeArchivedClient)
                    }
                  >
                    See Archive
                  </Button>
                  <Button
                    borderColor="primary.900"
                    background="background.secondary"
                    color="primary.900"
                    borderRadius="8px"
                    ml="16px"
                    fontSize="16px"
                    onClick={() =>
                      navigate(clientId ? `/job/create?client_id=${clientId}` : '/job/create')
                    }
                  >
                    <Plus size={24} />
                    Add New
                  </Button>
                </Flex>
              )}
            </Flex>
            <Box h="24px" />
            {fetchDataError ? (
              <Flex direction="column" justify="center" align="center" height="100%">
                <Text
                  fontSize="20px"
                  fontWeight="bold"
                  color="error.700"
                  textAlign="center"
                  mb="8px"
                >
                  Unable to load data.
                </Text>
                <Text fontSize="16px" color="error.700" textAlign="center">
                  Please try again later or contact support for assistance.
                </Text>
              </Flex>
            ) : seeJobBoard ? (
              <>
                <Table.Root variant="line">
                  <Table.Header>
                    <TableHeader headers={['Job ID', 'Category', 'Client', 'Job Title']} />
                  </Table.Header>
                  {!listLoading && (
                    <Table.Body>
                      {jobs.map((job) => (
                        <JobBoardTableRow
                          key={job.job_id}
                          job={job}
                          onNavigate={handleNavigateToJob}
                          onPublish={handlePublishJob}
                        />
                      ))}
                    </Table.Body>
                  )}
                </Table.Root>
                {!listLoading && jobs.length > 0 && (
                  <Flex justifyContent="flex-end" alignItems="center" mt="16px">
                    <PaginationRoot
                      count={jobTotalPages * pageSize}
                      pageSize={pageSize}
                      page={jobCurrentPage}
                      onPageChange={changeJobBoardPage}
                    >
                      <Stack wrap="wrap" direction="row">
                        <PaginationPrevTrigger />
                        <Text fontSize="14px" color="neutrals.700" alignSelf="center" mx="8px">
                          {jobCurrentPage} / {jobTotalPages}
                        </Text>
                        <PaginationNextTrigger />
                      </Stack>
                    </PaginationRoot>
                  </Flex>
                )}

                {listLoading || pageLoading ? (
                  <Flex flexDir="column" alignItems="center" justifyContent="center" py="120px">
                    <Spinner boxSize="32px" borderWidth="4px" color="primary.700" />
                  </Flex>
                ) : (
                  jobs.length === 0 && (
                    <Flex flexDir="column" alignItems="center" justifyContent="center">
                      <Image src={emptyJobImage} h="280px" w="280px" mt="64px" />
                      <Text fontSize="20px" fontWeight="bold" color="neutrals.700">
                        No Job Published on Job Board
                      </Text>
                      <Text fontSize="16px" color="neutrals.500">
                        No jobs are currently published on the job board.
                      </Text>
                    </Flex>
                  )
                )}
              </>
            ) : clientId ? (
              <>
                <Table.Root variant="line">
                  <Table.Header>
                    <TableHeader headers={['Job ID', 'Job Title', 'Posted Date', 'Status']} />
                  </Table.Header>
                  {!listLoading && (
                    <Table.Body>
                      {jobs.map((job) => (
                        <JobTableRow
                          key={job.job_id}
                          job={job}
                          onArchive={handleArchiveJob}
                          isArchived={seeArchivedJob}
                          onNavigate={handleNavigateToJob}
                          onPublish={handlePublishJob}
                        />
                      ))}
                    </Table.Body>
                  )}
                </Table.Root>
                {!listLoading && jobs.length > 0 && (
                  <Flex justifyContent="flex-end" alignItems="center" mt="16px">
                    <PaginationRoot
                      count={jobTotalPages * pageSize}
                      pageSize={pageSize}
                      page={jobCurrentPage}
                      onPageChange={changeJobsPage}
                    >
                      <Stack wrap="wrap" direction="row">
                        <PaginationPrevTrigger />
                        <Text fontSize="14px" color="neutrals.700" alignSelf="center" mx="8px">
                          {jobCurrentPage} / {jobTotalPages}
                        </Text>
                        <PaginationNextTrigger />
                      </Stack>
                    </PaginationRoot>
                  </Flex>
                )}

                {listLoading || pageLoading ? (
                  <Flex flexDir="column" alignItems="center" justifyContent="center" py="120px">
                    <Spinner boxSize="32px" borderWidth="4px" color="primary.700" />
                  </Flex>
                ) : (
                  jobs.length === 0 && (
                    <Flex flexDir="column" alignItems="center" justifyContent="center">
                      <Image src={emptyJobImage} h="280px" w="280px" mt="64px" />
                      <Text fontSize="20px" fontWeight="bold" color="neutrals.700">
                        No Job Posted
                      </Text>
                      <Text fontSize="16px" color="neutrals.500">
                        Let's get started with your first job posting.
                      </Text>
                      <Box h="16px" />
                      <Button
                        w="240px"
                        h="48px"
                        borderRadius="8px"
                        bg="primary.900"
                        fontSize="16px"
                        fontWeight="regular"
                        onClick={() => navigate('/job/create')}
                      >
                        <Plus size={24} />
                        Add new job
                      </Button>
                    </Flex>
                  )
                )}
              </>
            ) : (
              <>
                <Table.Root>
                  <Table.Header>
                    <TableHeader headers={['Client ID', 'Client Name', 'Industry']} />
                  </Table.Header>
                  <Table.Body>
                    {!listLoading &&
                      clients.map((client) => (
                        <ClientTableRow
                          key={client.client_id}
                          client={client}
                          onSelect={selectClient}
                          onArchive={handleArchiveClient}
                          onEdit={handleEditClient}
                          isArchived={seeArchivedClient}
                        />
                      ))}
                  </Table.Body>
                </Table.Root>
                {editClientProfileClient && (
                  <EditClientProfile
                    open={editClientProfileOpen}
                    setOpen={setEditClientProfileOpen}
                    client={editClientProfileClient}
                    onUpdateClient={updateClient}
                  />
                )}
                {!listLoading && clients.length > 0 && (
                  <Flex justifyContent="flex-end" alignItems="center" mt="16px">
                    <PaginationRoot
                      count={clientTotalPages * pageSize}
                      pageSize={pageSize}
                      page={clientCurrentPage}
                      onPageChange={changeClientsPage}
                    >
                      <Stack wrap="wrap" direction="row">
                        <PaginationPrevTrigger />
                        <Text fontSize="14px" color="neutrals.700" alignSelf="center" mx="8px">
                          {clientCurrentPage} / {clientTotalPages}
                        </Text>
                        <PaginationNextTrigger />
                      </Stack>
                    </PaginationRoot>
                  </Flex>
                )}

                {listLoading || pageLoading ? (
                  <Flex flexDir="column" alignItems="center" justifyContent="center" py="120px">
                    <Spinner boxSize="32px" borderWidth="4px" color="primary.700" />
                  </Flex>
                ) : (
                  clients.length === 0 && (
                    <Flex flexDir="column" alignItems="center" justifyContent="center">
                      <Image src={emptyJobImage} h="280px" w="280px" mt="64px" />
                      <Text fontSize="20px" fontWeight="bold" color="neutrals.700">
                        No Job Posted
                      </Text>
                      <Text fontSize="16px" color="neutrals.500">
                        Let's get started with your first job posting.
                      </Text>
                      <Box h="16px" />
                      <Button
                        w="240px"
                        h="48px"
                        borderRadius="8px"
                        bg="primary.900"
                        fontSize="16px"
                        fontWeight="regular"
                        onClick={() => navigate('/job/create')}
                      >
                        <Plus size={24} />
                        Add new job
                      </Button>
                    </Flex>
                  )
                )}
              </>
            )}
            {archiveType && archiveItem && (
              <ArchiveDialog
                open={archiveDialogOpen}
                setOpen={setArchiveDialogOpen}
                onArchiveClick={async (): Promise<number> => {
                  try {
                    const response =
                      archiveType === ArchiveType.Job
                        ? await archiveJob(archiveItem as JobBasicInfo, !isArchived)
                        : await archiveClient(archiveItem as Client, !isArchived);
                    return response;
                  } catch (error) {
                    console.error('Failed to archive job:', error);
                    throw error;
                  }
                }}
                archived={!isArchived}
                archiveType={archiveType}
              />
            )}
            <PublishJobDialog
              isOpen={publishDialogOpen}
              onClose={handlePublishCancel}
              onConfirm={handlePublishConfirm}
              job={publishJob}
              isUpdating={toUpdateCategory}
              isPublishing={toPublish}
              isLoading={isPublishUpdating}
              error={publishError}
            />
          </Flex>
        </Box>
      )}
    </Flex>
  );
}
